import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { Alert, Button, Spin } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotateRight } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { userPrivilagesAtom } from '../../../atoms/Atoms';
import MainPage from '../../shared-components/main-page/MainPage';
import { H1 } from '../../shared-components/typography/Title';
import { Spinner } from '../../shared-components/Spinner';
import StatTile from '../../shared-components/analytics/StatTile';
import { getWarehouseDashboard } from '../../../warehouse/warehouseApi';
import StatusCard from './StatusCard';
import NightHistory from './NightHistory';
import RunsTable from './RunsTable';
import { TableFreshness, TransformChain, BackfillProgress } from './WarehouseDetails';
import { fmtUtc, fmtUtcFull, fmtDate, fmtDuration } from './format';

// Data Warehouse -- load status and run history. Global Admin or Data Warehouse
// Health only.
//
// The API refuses everyone else on its own (WarehouseStatusService checks the
// role), so the redirect below is courtesy, not security. It waits until the
// privileges have actually been computed: on a hard refresh the atom starts as
// {} and a bare falsy check would bounce an allowed user to /home.
function DataWarehouse() {
  const { t } = useTranslation();
  const privileges = useRecoilValue(userPrivilagesAtom);
  const privilegesKnown = typeof privileges?.hasDataWarehouseHealthPrivilages === 'boolean';
  const canView = privileges?.hasDataWarehouseHealthPrivilages === true;

  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  function load() {
    setLoading(true);
    setError(null);
    getWarehouseDashboard()
      .then((d) => { setDashboard(d); setLoading(false); })
      .catch((err) => { setError(err.message); setLoading(false); });
  }

  useEffect(() => {
    if (canView) load();
  }, [canView]);

  if (privilegesKnown && !canView) {
    return <Navigate to="/home" replace />;
  }

  const status = dashboard?.status;
  const verdictWord = status?.lastGateVerdict?.split(' ')[0];
  // After the legacy cutover the gate writes no verdicts: the soak streak and the
  // last verdict are history, and the nightly's check against the source replaces both tiles.
  const cutOver = status?.isCutOver === true;

  // A gap inside the streak outranks "signed off": the count may be overstating it.
  let streakSub = t('dw_nights_to_go', { count: status?.nightsRemaining ?? 0 });
  if (dashboard?.streakMissingNights > 0) {
    streakSub = t('dw_streak_gaps', { nights: dashboard.streakCalendarNights, missing: dashboard.streakMissingNights });
  } else if (status?.isSignedOff) {
    streakSub = t('analytics_signed_off');
  }

  return (
    <MainPage>
      <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
        <div>
          <H1 text={t('data_warehouse')} />
          <p className="mt-1 max-w-3xl text-sm text-muted">{t('data_warehouse_subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          {dashboard?.generatedAtUtc && (
            <span className="text-xs text-muted tabular-nums">
              {t('dw_as_of')} {fmtUtcFull(dashboard.generatedAtUtc)} UTC
            </span>
          )}
          <Button size="small" onClick={load} loading={loading} icon={<FontAwesomeIcon icon={faRotateRight} />}>
            {t('reload')}
          </Button>
        </div>
      </div>

      {error && <Alert className="mt-4" type="error" showIcon message={error} />}

      <Spin spinning={loading && !dashboard} indicator={<Spinner className="text-accentColor" big />}>
        <div className="mt-4 min-h-40 space-y-5">
          {dashboard && (
            <>
              <StatusCard status={status} />

              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <StatTile
                  label={t('dw_last_scheduled_load')}
                  value={status?.lastRunStatus ?? '—'}
                  sub={status?.lastRunId != null
                    ? `${t('dw_run')} ${status.lastRunId} · ${fmtDuration(status.lastRunDurationSeconds)} · ${fmtUtc(status.lastRunStartedAtUtc)} UTC`
                    : null}
                  hint={t('dw_last_scheduled_load_help')}
                  emphasis
                />
                <StatTile
                  label={t('dw_pages_current_to')}
                  value={fmtDate(status?.latestSalesDate)}
                  sub={t('dw_pages_current_to_sub')}
                  hint={t('dw_pages_current_to_help')}
                />
                {cutOver ? (
                  <>
                    <StatTile
                      label={t('dw_source_check')}
                      value={status?.lastSourceCheckStatus ?? t('dw_source_check_not_run')}
                      sub={status?.lastSourceCheckAtUtc
                        ? `${t('dw_run')} ${status.lastRunId} · ${fmtUtc(status.lastSourceCheckAtUtc)} UTC`
                        : t('dw_source_check_not_run_sub')}
                      hint={t('dw_source_check_help')}
                    />
                    <StatTile
                      label={t('dw_last_gate')}
                      value={t('dw_gate_retired')}
                      sub={status?.lastGateRecordedAtUtc
                        ? `${t('dw_gate_last_verdict')} ${fmtUtc(status.lastGateRecordedAtUtc)} UTC`
                        : null}
                      hint={t('dw_gate_retired_help')}
                    />
                  </>
                ) : (
                  <>
                    <StatTile
                      label={t('dw_soak_streak')}
                      value={`${status?.consecutivePassingNights ?? 0} / ${status?.nightsRequired ?? '—'}`}
                      sub={streakSub}
                      hint={t('dw_soak_streak_help')}
                    />
                    <StatTile
                      label={t('dw_last_gate')}
                      value={verdictWord ?? '—'}
                      sub={status?.lastGateRecordedAtUtc
                        ? `${t('dw_unexplained_short', { count: status.lastGateUnexplained ?? 0 })} · ${fmtUtc(status.lastGateRecordedAtUtc)} UTC`
                        : null}
                      hint={status?.lastGateVerdict}
                    />
                  </>
                )}
              </div>

              {!cutOver && dashboard.streakMissingNights > 0 && (
                <Alert
                  type="warning"
                  showIcon
                  message={t('dw_streak_gaps_title')}
                  description={t('dw_streak_gaps_desc', { nights: dashboard.streakCalendarNights, missing: dashboard.streakMissingNights })}
                />
              )}

              <NightHistory nights={dashboard.nights} cutOver={cutOver} />

              <RunsTable runs={dashboard.recentRuns} cutOver={cutOver} />

              <div className="grid gap-5 xl:grid-cols-2">
                <TableFreshness tables={dashboard.tables} />
                <TransformChain steps={dashboard.transformSteps} />
              </div>

              <BackfillProgress backfill={dashboard.backfill} />
            </>
          )}
        </div>
      </Spin>
    </MainPage>
  );
}

export default DataWarehouse;
